import React, { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { Anchor, ContainerDiv, DefaultButton, FjCard, FjText } from 'src/components/Common'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import { Analytics } from 'src/utils/Analytics'

const GEO_LOCATION_KEY = 'GEO_LOCATION_KEY'
const COOKIE_CONSENT_KEY = 'COOKIE_CONSENT_KEY'

const DATA_PROTECTION_LAW_COUNTRIES = [
  // GDPR
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IS',
  'IE',
  'IT',
  'LV',
  'LI',
  'LT',
  'LU',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'CH',
  'GB',
  'UK',
  // PIPEDA
  'CA',
  // LGPD
  'BR',
]

const GEO_LOOKUP_URL = `https://pro.ip-api.com/json?key=${process.env.REACT_APP_IP_API_KEY || ''}`
/*
SAMPLE RESPONSE:
{
  "as": ,
  "city": ,
  "country": ,
  "countryCode": <2 letter country code>,
  "isp": ,
  "lat": ,
  "lon": ,
  "org": ,
  "query": <ip address>,
  "region": <provice/state abbr>,
  "regionName": <provice/state>,
  "status": ,
  "timezone": ,
  "zip": 
}
*/

const containerStyle: React.CSSProperties = {
  position: 'fixed',
  right: '1em',
  bottom: '1em',
  width: 300,
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1000,
}

export const CookieConsentManager: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false)

  const getLocation = useCallback(async () => {
    // Check localStorage first
    const storedGeoData = localStorage.getItem(GEO_LOCATION_KEY)
    if (storedGeoData) {
      const parsedData = JSON.parse(storedGeoData)

      // If data hasn't expired, return it
      if (moment().isBefore(moment(parsedData.expiry))) {
        return parsedData
      }
    }

    // Fetch new data if no stored data or data has expired
    const res = await fetch(GEO_LOOKUP_URL)
    const data = await res.json()

    if (data.status === 'fail') throw new Error(data.message)

    // Compliance doesn't allow us to store all the data so we store
    // a subset and add expiry date (30 days from now) to account for people
    // moving around/vpns/etc.
    const storedData = {
      countryCode: data.countryCode,
      timezone: data.timezone,
      expiry: moment().add(30, 'day').toISOString(),
    }

    localStorage.setItem(GEO_LOCATION_KEY, JSON.stringify(storedData))

    return storedData
  }, [])

  const showBannerIfNecessary = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === 'development') return

      const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY)
      if (storedConsent === 'accepted') {
        Analytics.loadAnalytics()
        return
      }

      // user has previously rejected
      if (storedConsent === 'declined') return

      const geoData = await getLocation()

      if (DATA_PROTECTION_LAW_COUNTRIES.includes(geoData.countryCode)) setShowBanner(true)
      else Analytics.loadAnalytics()
    } catch (err) {
      sharedAppStateStore.handleError(err, { alert: false })
      // If we fail to retrieve the geo data we fall back to adhere to most
      // strict regulations and prompt the user again
      setShowBanner(true)
    }
  }, [getLocation])

  useEffect(() => {
    showBannerIfNecessary()
  }, [showBannerIfNecessary])

  const accept = async () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted')
    setShowBanner(false)
    Analytics.loadAnalytics()
    let geoData = {}
    try {
      geoData = await getLocation()
    } catch {
      // do nothing
    }

    Analytics.trackCookieConsentResponse('accepted', geoData)
  }

  const decline = async () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined')
    setShowBanner(false)
    let geoData = {}
    try {
      geoData = await getLocation()
    } catch {
      // do nothing
    }
    Analytics.trackCookieConsentResponse('declined', geoData)
  }

  if (!showBanner) return null

  return (
    <FjCard style={containerStyle}>
      <FjText fontWeight="semi-bold" display="block" textAlign="left" marginBottom="5px">
        Cookie Settings
      </FjText>
      <p>
        We use cookies analyze site traffic. Click{' '}
        <Anchor href="https://flockjay.com/privacy-policy" target="_blank">
          here
        </Anchor>{' '}
        to learn more. Some cookies are essential for site operation.
      </p>
      <ContainerDiv display="flex" gap={10} justifyContent="end">
        <DefaultButton clicked={decline} buttonType="secondary" title="Reject" size="small" width="125px" />
        <DefaultButton clicked={accept} buttonType="primary" title="Accept" size="small" width="125px" />
      </ContainerDiv>
    </FjCard>
  )
}
